import useApiRequest from "@/lib/hooks/use-request";
import { SalesRankProvisionsResponseDto } from "@/lib/interfaces/sales-ranks";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { PageTitle, PageTitleContainer } from "@/ui-components/utils/menu-items/page-title";
import { useCallback, useEffect, useState } from "react";

const SalesRankDetails = () => {
  return (
    <div>
      <PageTitleContainer>
        <div className="flex flex-row justify-between items-center">
          <PageTitle>Verkaufsränge Informationen</PageTitle>
        </div>
      </PageTitleContainer>
      <div className="p-4 items-center m-auto">
        <SalesRankDetailsFetcher />
      </div>
    </div>
  );
};

const SalesRankDetailsFetcher = () => {
  const { apiRequest } = useApiRequest();
  const [salesRank, setSalesRank] = useState<SalesRankProvisionsResponseDto[]>();
  const [isLoading, setIsLoading] = useState(false);

  const getSalesRank = useCallback(async () => {
    setIsLoading(true);
    const response = await apiRequest<SalesRankProvisionsResponseDto[]>("settings/sales-ranks/provision-steps", "GET");
    if (response.data) {
      setSalesRank(response.data);
    }
    setIsLoading(false);
  }, [apiRequest]);

  useEffect(() => {
    getSalesRank();
  }, [getSalesRank]);

  return (
    <DataFetchParent
      isLoading={isLoading}
      data={salesRank}
      renderElement={(item: SalesRankProvisionsResponseDto[]) => <SalesRankPreview salesRank={item} />}
    />
  );
};

const SalesRankPreview = ({ salesRank }: { salesRank: SalesRankProvisionsResponseDto[] }) => {
  const maxSteps = Math.max(...salesRank.map((rank) => rank.provisionSteps.length)) + 1;

  const bgColors = ["bg-red-300", "bg-red-400", "bg-red-500", "bg-red-600", "bg-red-700", "bg-red-800", "bg-red-900"];

  const createGridRow = (rank: SalesRankProvisionsResponseDto) => {
    const row = new Array(maxSteps).fill(null);
    rank.provisionSteps.forEach((step, index) => {
      row[row.length - 1 - index] = step;
    });
    const firstContentIndex = row.findIndex((step) => step?.provision);
    return {
      title: rank.title,
      totalProvision: rank.totalProvision,
      steps: row,
      firstContentIndex,
      salesSpanMin: rank.salesSpanMin,
      salesSpanMax: rank.salesSpanMax,
    };
  };

  const gridData = salesRank.map(createGridRow);

  return (
    <div className="w-full max-w-6xl">
      <div className="flex flex-row">
        {/* Provision Steps Grid */}
        {gridData.map((row, rowIndex) => {
          return (
            <div key={rowIndex} className="flex flex-col grow">
              {row.steps.map((step, colIndex) => {
                let localTitle = false;
                if (!step?.provision && row.firstContentIndex - 1 === colIndex) {
                  localTitle = true;
                }

                return (
                  <div key={colIndex} className="flex-1">
                    <div
                      className={`p-2 text-sm text-white text-center
                        ${
                          step
                            ? bgColors[bgColors.length - colIndex - 1]
                            : localTitle
                            ? `${bgColors[bgColors.length - colIndex - 2]} border-b-2 border-white`
                            : "bg-white"
                        }`}
                    >
                      {step?.provision ? (
                        `${step.provision}%`
                      ) : localTitle ? (
                        <>
                          {row.title}
                          <br />
                          {row.totalProvision}%
                        </>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="text-sm text-gray-600 text-center mt-2 p-2 bg-gray-100 rounded">
                {row.salesSpanMin === row.salesSpanMax 
                  ? `ab ${row.salesSpanMin} Verkäufe`
                  : `${row.salesSpanMin} - ${row.salesSpanMax} Verkäufe`
                }
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SalesRankDetails;
